<template>
  <view-section
    expandable
    :title="$t('reuse_permissions.heading')"
    :toggled="toggled"
    @update:toggle="setToggle"
  >
    <template v-for="({ text, permitted }, index) in permissions" :key="index">
      <base-divider
        v-if="index != 0"
        class="tw-my-1"
        :class="[
          Classes.FILE_DIVIDER,
          'tw-border-t tw-border-[var(--tile-text)] tw-opacity-30',
        ]"
      />

      <div class="tw-flex tw-items-center tw-gap-2">
        <font-awesome-icon
          :icon="
            permitted === PERMITTED.ALL
              ? 'check-circle'
              : permitted === PERMITTED.PARTIAL
                ? 'exclamation-circle'
                : 'times-circle'
          "
          :class="
            permitted == PERMITTED.ALL
              ? 'tw-text-success'
              : permitted == PERMITTED.PARTIAL
                ? 'tw-text-warning'
                : 'tw-text-danger'
          "
        />
        <span>{{ text }}</span>
      </div>
    </template>
  </view-section>
</template>
<script>
import ViewSection from '@components/View/ViewSection.vue'
import { useResourceValue } from '@composables'
import { reusePermissionsStore } from '@stores'
import Classes from '@shared/js/config/css.js'
import { computed, toRefs } from 'vue'
import {
  allowRepackageWithPermission,
  allowPatchingWithPermission,
  isOriginalOrPermittedAssets,
  allowRepackageWithCredit,
  allowPatchingWithCredit,
  allowRepackageNoCredit,
  allowPatchingNoCredit,
  allowRedistribution,
  allowUsersToResell,
  allowPorting,
} from '@helpers/bitHelper.js'

const PERMITTED = {
  NONE: 0,
  PARTIAL: 1,
  ALL: 2,
}

export default {
  components: {
    ViewSection,
  },
  props: {
    game: {
      type: Object,
      default: null,
    },
    mod: {
      type: Object,
      default: null,
    },
  },
  setup(props) {
    const { game, mod } = toRefs(props)
    const { ugcName } = useResourceValue()
    const { setToggle, toggled } = reusePermissionsStore()

    const permissions = computed(() => {
      if (!mod.value) return []

      const _isOriginalOrPermittedAssets = isOriginalOrPermittedAssets(
        mod.value.credit_options
      )
      const _allowRedistribution = allowRedistribution(mod.value.credit_options)
      const _allowPorting = allowPorting(mod.value.credit_options)
      const _allowPatchingNoCredit = allowPatchingNoCredit(
        mod.value.credit_options
      )
      const _allowPatchingWithCredit = allowPatchingWithCredit(
        mod.value.credit_options
      )
      const _allowPatchingWithPermission = allowPatchingWithPermission(
        mod.value.credit_options
      )
      const _allowRepackageNoCredit = allowRepackageNoCredit(
        mod.value.credit_options
      )
      const _allowRepackageWithCredit = allowRepackageWithCredit(
        mod.value.credit_options
      )
      const _allowRepackageWithPermission = allowRepackageWithPermission(
        mod.value.credit_options
      )
      const _allowUsersToResell = allowUsersToResell(mod.value.credit_options)

      return [
        {
          permitted: _isOriginalOrPermittedAssets
            ? PERMITTED.ALL
            : PERMITTED.NONE,
          text: _isOriginalOrPermittedAssets
            ? 'All assets are 100% original work or are free-to-use resources'
            : 'Not all assets are 100% original work or are free-to-use resources',
        },
        {
          permitted: _allowRedistribution ? PERMITTED.ALL : PERMITTED.NONE,
          text: _allowRedistribution
            ? 'Upload to other sites is allowed, but the creator(s) must be credited'
            : 'Upload to other sites is not allowed',
        },
        {
          permitted: _allowPorting ? PERMITTED.ALL : PERMITTED.NONE,
          text: _allowPorting
            ? 'Conversion to other games is allowed, but the creator(s) must be credited'
            : 'Conversion to other games is not allowed',
        },
        {
          permitted: _allowPatchingNoCredit
            ? _allowPatchingWithCredit || _allowPatchingWithPermission
              ? PERMITTED.PARTIAL
              : PERMITTED.ALL
            : PERMITTED.NONE,
          text: getPermissionText(
            _allowPatchingNoCredit,
            `Changing, fixing or remastering this ${
              ugcName(game.value, true).value
            } is allowed`,
            _allowPatchingWithCredit,
            _allowPatchingWithPermission
          ),
        },
        {
          permitted: _allowRepackageNoCredit
            ? _allowRepackageWithCredit || _allowRepackageWithPermission
              ? PERMITTED.PARTIAL
              : PERMITTED.ALL
            : PERMITTED.NONE,
          text: getPermissionText(
            _allowRepackageNoCredit,
            `Use of assets in other ${
              ugcName(game.value, false).value
            } is allowed`,
            _allowRepackageWithCredit,
            _allowRepackageWithPermission
          ),
        },
        {
          permitted: _allowUsersToResell ? PERMITTED.ALL : PERMITTED.NONE,
          text: _allowUsersToResell
            ? 'Use of assets commercially is allowed'
            : 'Use of assets commercially is not allowed',
        },
      ]
    })

    function getPermissionText(
      allowed,
      allowedText,
      withCredit = false,
      withPermission = false
    ) {
      if (allowed) {
        if (withCredit && withPermission)
          return `${allowedText}, but requires permission and the creator(s) must be credited`

        if (withCredit)
          return `${allowedText}, but the creator(s) must be credited`

        if (withPermission) return `${allowedText}, but requires permission`

        return allowedText
      }

      return allowedText.replace('is allowed', 'is not allowed')
    }

    return {
      permissions,
      PERMITTED,
      setToggle,
      toggled,
      Classes,
    }
  },
}
</script>
